import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BookOpen, Upload, Copy, Trash2, Eye, EyeOff, Users, Calendar, FileText, AlertCircle, CheckCircle, Download, ArrowLeft, X, Shield, Pencil } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Papa from 'papaparse';
import ReadingPlanEditor from './components/ReadingPlanEditor';

const ReadingPlanManagement = () => {
  const navigate = useNavigate();
  const { userProfile, isDemoAdmin } = useAuth();
  const fileInputRef = useRef(null);

  // State management
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [plans, setPlans] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);

  // Form state for plan metadata
  const [planMetadata, setPlanMetadata] = useState({
    title: '',
    description: '',
    category: 'Chronological',
    tags: [],
    estimatedDailyReadingTime: 15
  });

  // CSV data state
  const [csvData, setCsvData] = useState([]);
  const [detectedHeaders, setDetectedHeaders] = useState([]);

  useEffect(() => {
    checkAdminAccess();
  }, [userProfile, isDemoAdmin]);

  const checkAdminAccess = async () => {
    try {
      setLoading(true);
      const contextIsAdmin = userProfile?.role === 'admin';
      const contextIsDemoAdmin = isDemoAdmin === true;

      if (contextIsAdmin || contextIsDemoAdmin) {
        setIsAdmin(true);
        await loadReadingPlans();
        return;
      }

      try {
        const adminStatus = await adminService?.isAdmin();
        if (adminStatus) {
          setIsAdmin(true);
          await loadReadingPlans();
          return;
        }
      } catch (serviceError) {
        console.warn('AdminService check failed:', serviceError);
      }

      setError('Access denied. Administrator privileges required.');
      setLoading(false);
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      console.error('Admin access check failed:', err);
      setError('Access verification failed. Redirecting to dashboard.');
      setLoading(false);
      setTimeout(() => navigate('/dashboard'), 2000);
    }
  };

  const loadReadingPlans = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('reading_plans')?.select(`
          *,
          user_reading_plans(count)
        `)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data to include subscriber count
      const transformedPlans = data?.map(plan => ({
        ...plan,
        subscriberCount: plan?.user_reading_plans?.[0]?.count || 0
      }));

      setPlans(transformedPlans || []);
    } catch (err) {
      console.error('Error loading reading plans:', err);
      setError('Failed to load reading plans');
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileSelect(e?.dataTransfer?.files?.[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFileSelect(e?.target?.files?.[0]);
    }
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file?.name?.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setSelectedFile(file);
    setError('');

    // Parse CSV for preview
    const reader = new FileReader();
    reader.onload = async (e) => {
      const csvContent = e?.target?.result;
      Papa?.parse(csvContent, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results?.errors?.length > 0) {
            setError(`CSV parsing errors: ${results?.errors?.map(e => e?.message)?.join(', ')}`);
            return;
          }

          const headers = results?.meta?.fields || [];
          setDetectedHeaders(headers);
          setCsvData(results?.data);

          // Validate CSV structure
          const requiredColumns = ['day_number', 'book_name', 'chapter'];
          const missingColumns = requiredColumns?.filter(col => !headers?.includes(col));

          if (missingColumns?.length > 0) {
            setError(`Missing required columns: ${missingColumns?.join(', ')}. Required: day_number, book_name, chapter`);
            return;
          }

          // Show preview
          setPreviewData({
            headers,
            rows: results?.data?.slice(0, 5),
            totalRows: results?.data?.length
          });

          // Validate Bible references
          validateBibleReferences(results?.data);
        }
      });
    };
    reader?.readAsText(file);
  };

  const validateBibleReferences = async (data) => {
    const errors = [];
    const bibleBooks = await getBibleBooks();
    const bookNames = bibleBooks?.map(b => b?.name?.toLowerCase());

    data?.forEach((row, index) => {
      const bookName = row?.book_name?.toLowerCase()?.trim();
      const chapter = parseInt(row?.chapter);

      if (!bookNames?.includes(bookName)) {
        errors?.push(`Row ${index + 1}: Invalid book name "${row?.book_name}"`);
      }

      if (isNaN(chapter) || chapter < 1) {
        errors?.push(`Row ${index + 1}: Invalid chapter number "${row?.chapter}"`);
      }

      // Validate verse ranges if provided
      if (row?.verse_start) {
        const verseStart = parseInt(row?.verse_start);
        if (isNaN(verseStart) || verseStart < 1) {
          errors?.push(`Row ${index + 1}: Invalid verse_start "${row?.verse_start}"`);
        }
      }

      if (row?.verse_end) {
        const verseEnd = parseInt(row?.verse_end);
        const verseStart = parseInt(row?.verse_start || 1);
        if (isNaN(verseEnd) || verseEnd < verseStart) {
          errors?.push(`Row ${index + 1}: Invalid verse_end "${row?.verse_end}"`);
        }
      }
    });

    setValidationErrors(errors);
  };

  const getBibleBooks = async () => {
    try {
      const { data, error } = await supabase?.from('bible_books')?.select('name, book_number')?.order('book_number');

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching Bible books:', err);
      return [];
    }
  };

  const handleImportPlan = async () => {
    if (!selectedFile || !csvData?.length) {
      setError('Please select a valid CSV file');
      return;
    }

    if (validationErrors?.length > 0) {
      setError('Please fix validation errors before importing');
      return;
    }

    if (!planMetadata?.title || !planMetadata?.description) {
      setError('Please fill in plan title and description');
      return;
    }

    try {
      setImporting(true);
      setError('');

      // Calculate duration from CSV data
      const maxDay = Math.max(...csvData?.map(row => parseInt(row?.day_number) || 0));

      // Create reading plan
      const { data: planData, error: planError } = await supabase?.from('reading_plans')?.insert({
          name: planMetadata?.title,
          description: planMetadata?.description,
          category: planMetadata?.category,
          duration_days: maxDay,
          total_readings: csvData?.length,
          tags: planMetadata?.tags,
          estimated_daily_reading_time: planMetadata?.estimatedDailyReadingTime,
          is_public: true
        })?.select()?.single();

      if (planError) throw planError;

      // Group CSV data by day
      const dayGroups = {};
      csvData?.forEach(row => {
        const dayNum = parseInt(row?.day_number);
        if (!dayGroups?.[dayNum]) {
          dayGroups[dayNum] = [];
        }
        dayGroups?.[dayNum]?.push(row);
      });

      // Insert days and references
      for (const [dayNumber, references] of Object.entries(dayGroups)) {
        // Create day
        const { data: dayData, error: dayError } = await supabase?.from('reading_plan_days')?.insert({
            reading_plan_id: planData?.id,
            day_number: parseInt(dayNumber),
            title: `Day ${dayNumber}`,
            description: '',
            display_order: parseInt(dayNumber)
          })?.select()?.single();

        if (dayError) throw dayError;

        // Insert references for this day
        const referenceInserts = references?.map((ref, index) => {
          const verseStart = ref?.verse_start ? parseInt(ref?.verse_start) : null;
          const verseEnd = ref?.verse_end ? parseInt(ref?.verse_end) : null;

          // Build reference text
          let referenceText = `${ref?.book_name} ${ref?.chapter}`;
          if (verseStart && verseEnd) {
            referenceText += `:${verseStart}-${verseEnd}`;
          } else if (verseStart) {
            referenceText += `:${verseStart}`;
          }

          return {
            reading_plan_day_id: dayData?.id,
            book_name: ref?.book_name,
            chapter: parseInt(ref?.chapter),
            verse_start: verseStart,
            verse_end: verseEnd,
            reference_text: referenceText,
            display_order: index + 1
          };
        });

        const { error: refError } = await supabase?.from('reading_plan_references')?.insert(referenceInserts);

        if (refError) throw refError;
      }

      setSuccess(`Successfully imported reading plan: ${planMetadata?.title}`);
      setShowImportModal(false);
      resetImportForm();
      await loadReadingPlans();
    } catch (err) {
      console.error('Error importing plan:', err);
      setError(`Import failed: ${err?.message}`);
    } finally {
      setImporting(false);
    }
  };

  const resetImportForm = () => {
    setSelectedFile(null);
    setCsvData([]);
    setDetectedHeaders([]);
    setPreviewData(null);
    setValidationErrors([]);
    setPlanMetadata({
      title: '',
      description: '',
      category: 'Chronological',
      tags: [],
      estimatedDailyReadingTime: 15
    });
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm('Are you sure you want to delete this reading plan? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase?.from('reading_plans')?.delete()?.eq('id', planId);

      if (error) throw error;

      setSuccess('Reading plan deleted successfully');
      await loadReadingPlans();
    } catch (err) {
      console.error('Error deleting plan:', err);
      setError(`Failed to delete plan: ${err?.message}`);
    }
  };

  const handleDuplicatePlan = async (plan) => {
    try {
      // Create duplicate plan
      const { data: newPlan, error: planError } = await supabase?.from('reading_plans')?.insert({
          name: `${plan?.name} (Copy)`,
          description: plan?.description,
          category: plan?.category,
          duration_days: plan?.duration_days,
          total_readings: plan?.total_readings,
          tags: plan?.tags,
          estimated_daily_reading_time: plan?.estimated_daily_reading_time,
          is_public: false
        })?.select()?.single();

      if (planError) throw planError;

      // Get all days and references from original plan
      const { data: originalDays, error: daysError } = await supabase?.from('reading_plan_days')?.select(`
          *,
          reading_plan_references(*)
        `)?.eq('reading_plan_id', plan?.id)?.order('day_number');

      if (daysError) throw daysError;

      // Duplicate days and references
      for (const day of originalDays) {
        const { data: newDay, error: dayError } = await supabase?.from('reading_plan_days')?.insert({
            reading_plan_id: newPlan?.id,
            day_number: day?.day_number,
            title: day?.title,
            description: day?.description,
            display_order: day?.display_order
          })?.select()?.single();

        if (dayError) throw dayError;

        // Duplicate references
        if (day?.reading_plan_references?.length > 0) {
          const referenceInserts = day?.reading_plan_references?.map(ref => ({
            reading_plan_day_id: newDay?.id,
            book_name: ref?.book_name,
            book_id: ref?.book_id,
            chapter: ref?.chapter,
            verse_start: ref?.verse_start,
            verse_end: ref?.verse_end,
            reference_text: ref?.reference_text,
            display_order: ref?.display_order
          }));

          const { error: refError } = await supabase?.from('reading_plan_references')?.insert(referenceInserts);

          if (refError) throw refError;
        }
      }

      setSuccess(`Successfully duplicated plan: ${plan?.title}`);
      await loadReadingPlans();
    } catch (err) {
      console.error('Error duplicating plan:', err);
      setError(`Failed to duplicate plan: ${err?.message}`);
    }
  };

  const handleToggleVisibility = async (plan) => {
    try {
      const { error } = await supabase?.from('reading_plans')?.update({ is_public: !plan?.is_public })?.eq('id', plan?.id);

      if (error) throw error;

      setSuccess(`Plan visibility updated`);
      await loadReadingPlans();
    } catch (err) {
      console.error('Error toggling visibility:', err);
      setError(`Failed to update visibility: ${err?.message}`);
    }
  };

  const downloadCSVTemplate = () => {
    const template = [
      ['day_number', 'book_name', 'chapter', 'verse_start', 'verse_end', 'title', 'description'],
      ['1', 'Genesis', '1', '', '', 'Day 1: Creation', 'Read about the creation of the world'],
      ['1', 'Psalm', '1', '', '', '', ''],
      ['2', 'Genesis', '2', '', '', 'Day 2: Garden of Eden', ''],
      ['2', 'Matthew', '1', '1', '17', '', 'Genealogy of Jesus']
    ];

    const csv = Papa?.unparse(template);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reading_plan_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            {error || 'Verifying Administrator Access...'}
          </h2>
          <p className="text-muted-foreground mb-4">
            {error ? 'You will be redirected shortly.' : 'Please wait while we verify your privileges.'}
          </p>
          {!error && (
            <div className="flex items-center justify-center mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-sm text-primary hover:text-primary/80 underline"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card shadow-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <div className="flex items-center gap-3">
                <Link
                  to="/admin-dashboard"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Link>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Reading Plan Management</h1>
                  <p className="text-muted-foreground">Create and manage Bible reading plans</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={downloadCSVTemplate}>
                <Download className="w-4 h-4 mr-2" />Download Template
              </Button>
              <Button onClick={() => setShowImportModal(true)}>
                <Upload className="w-4 h-4 mr-2" />Import Plan
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success/Error Messages */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-destructive mr-2" />
              <span className="text-destructive">{error}</span>
              <button onClick={() => setError('')} className="ml-auto text-destructive hover:text-destructive/80">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-success/10 border border-success/30 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-success mr-2" />
              <span className="text-success">{success}</span>
              <button onClick={() => setSuccess('')} className="ml-auto text-success hover:text-success/80">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Plans Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">Loading reading plans...</p>
          </div>
        ) : plans?.length === 0 ? (
          <div className="bg-card rounded-lg shadow-sm p-12 text-center border border-border">
            <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Reading Plans Yet</h3>
            <p className="text-muted-foreground mb-6">Get started by importing your first reading plan</p>
            <Button onClick={() => setShowImportModal(true)}>
              <Upload className="w-4 h-4 mr-2" />Import Your First Plan
            </Button>
          </div>
        ) : (
          <div className="bg-card rounded-lg shadow-sm overflow-hidden border border-border">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Plan Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Subscribers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {plans?.map((plan) => (
                  <tr key={plan?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-foreground">{plan?.name}</div>
                        <div className="text-sm text-muted-foreground line-clamp-1">{plan?.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-foreground">
                        <Calendar className="w-4 h-4 mr-1 text-muted-foreground" />
                        {plan?.duration_days} days
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-accent/15 text-accent">
                        {plan?.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-foreground">
                        <Users className="w-4 h-4 mr-1 text-muted-foreground" />
                        {plan?.subscriberCount || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {plan?.is_public ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-success/15 text-success">
                          <Eye className="w-3 h-3 mr-1" />Public
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-muted text-muted-foreground">
                          <EyeOff className="w-3 h-3 mr-1" />Hidden
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(plan?.created_at)?.toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingPlan(plan)}
                          className="text-primary hover:text-primary/80 transition-colors p-1 rounded hover:bg-primary/10"
                          title="Edit plan"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleVisibility(plan)}
                          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted/50"
                          title={plan?.is_public ? 'Hide plan' : 'Make public'}
                        >
                          {plan?.is_public ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDuplicatePlan(plan)}
                          className="text-primary hover:text-primary/80 transition-colors p-1 rounded hover:bg-primary/10"
                          title="Duplicate plan"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePlan(plan?.id)}
                          className="text-destructive hover:text-destructive/80 transition-colors p-1 rounded hover:bg-destructive/10"
                          title="Delete plan"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reading Plan Editor Modal */}
      {editingPlan && (
        <ReadingPlanEditor
          plan={editingPlan}
          onClose={() => setEditingPlan(null)}
          onSaved={() => loadReadingPlans()}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-border">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground">Import Reading Plan</h2>
              <button
                onClick={() => { setShowImportModal(false); resetImportForm(); }}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Plan Metadata Form */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Plan Information</h3>
                <Input
                  label="Plan Title"
                  required
                  value={planMetadata?.title}
                  onChange={(e) => setPlanMetadata({ ...planMetadata, title: e?.target?.value })}
                  placeholder="e.g., Bible in a Year"
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                  <textarea
                    value={planMetadata?.description}
                    onChange={(e) => setPlanMetadata({ ...planMetadata, description: e?.target?.value })}
                    rows={3}
                    className="w-full rounded-md border border-border px-3 py-2 text-sm bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    placeholder="Describe the reading plan..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                    <select
                      value={planMetadata?.category}
                      onChange={(e) => setPlanMetadata({ ...planMetadata, category: e?.target?.value })}
                      className="w-full rounded-md border border-border px-3 py-2 text-sm bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    >
                      <option value="Chronological">Chronological</option>
                      <option value="Entire Bible">Entire Bible</option>
                      <option value="Topical">Topical</option>
                      <option value="New Testament">New Testament</option>
                      <option value="Old Testament">Old Testament</option>
                      <option value="Wisdom Literature">Wisdom Literature</option>
                      <option value="Gospels">Gospels</option>
                      <option value="Epistles">Epistles</option>
                      <option value="Christ">Christ</option>
                    </select>
                  </div>
                  <Input
                    label="Daily Reading Time (minutes)"
                    type="number"
                    value={planMetadata?.estimatedDailyReadingTime}
                    onChange={(e) => setPlanMetadata({ ...planMetadata, estimatedDailyReadingTime: parseInt(e?.target?.value) })}
                    min={1}
                  />
                </div>
              </div>

              {/* CSV Upload */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-foreground">Daily Readings (CSV)</h3>
                  <button
                    onClick={downloadCSVTemplate}
                    className="text-sm text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
                  >
                    <Download className="w-4 h-4" />Download Template
                  </button>
                </div>

                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-border/80'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileInput} className="hidden" />
                  {selectedFile ? (
                    <div className="space-y-2">
                      <FileText className="w-12 h-12 text-success mx-auto" />
                      <p className="text-sm font-medium text-foreground">{selectedFile?.name}</p>
                      <p className="text-xs text-muted-foreground">{(selectedFile?.size / 1024)?.toFixed(2)} KB</p>
                      <button
                        onClick={() => { setSelectedFile(null); setCsvData([]); setPreviewData(null); setValidationErrors([]); if (fileInputRef?.current) fileInputRef.current.value = ''; }}
                        className="text-sm text-destructive hover:text-destructive/80 transition-colors"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-12 h-12 text-muted-foreground mx-auto" />
                      <p className="text-sm text-muted-foreground">
                        Drag and drop your CSV file here, or{' '}
                        <button onClick={() => fileInputRef?.current?.click()} className="text-primary hover:text-primary/80 font-medium transition-colors">
                          browse
                        </button>
                      </p>
                      <p className="text-xs text-muted-foreground">Required columns: day_number, book_name, chapter</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Preview Data */}
              {previewData && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground">Preview</h3>
                  <div className="bg-muted/30 rounded-lg p-4 border border-border">
                    <p className="text-sm text-muted-foreground mb-2">
                      Total rows: {previewData?.totalRows} | Showing first 5 rows
                    </p>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            {previewData?.headers?.map((header, index) => (
                              <th key={index} className="px-3 py-2 text-left font-medium text-foreground">{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData?.rows?.map((row, rowIndex) => (
                            <tr key={rowIndex} className="border-b border-border/50">
                              {previewData?.headers?.map((header, colIndex) => (
                                <td key={colIndex} className="px-3 py-2 text-muted-foreground">{row?.[header] || '-'}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Validation Errors */}
              {validationErrors?.length > 0 && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-destructive mr-2 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-destructive mb-2">Validation Errors ({validationErrors?.length})</h4>
                      <ul className="text-sm text-destructive/80 space-y-1 max-h-40 overflow-y-auto">
                        {validationErrors?.slice(0, 10)?.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                        {validationErrors?.length > 10 && (
                          <li className="text-destructive font-medium">... and {validationErrors?.length - 10} more errors</li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Validation Success */}
              {csvData?.length > 0 && validationErrors?.length === 0 && (
                <div className="bg-success/10 border border-success/30 rounded-lg p-4">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-success mr-2" />
                    <span className="text-sm text-success">All Bible references validated successfully!</span>
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-muted/30 px-6 py-4 flex items-center justify-end gap-3 border-t border-border">
              <Button variant="outline" onClick={() => { setShowImportModal(false); resetImportForm(); }}>
                Cancel
              </Button>
              <Button
                onClick={handleImportPlan}
                loading={importing}
                disabled={!selectedFile || !csvData?.length || validationErrors?.length > 0 || !planMetadata?.title || !planMetadata?.description}
              >
                <Upload className="w-4 h-4 mr-2" />Import Plan
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadingPlanManagement;